import React, { useState, useRef } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import toast from 'react-hot-toast';
import { X, Calendar, Sparkles, User, Mail, Phone, MessageSquareText, Shield, ArrowRight, Loader2, Clock, Sliders } from 'lucide-react';
import styles from './BookDemoModal.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const PRESET_TIME_SLOTS = [
  '10:00 AM',
  '11:30 AM',
  '02:00 PM',
  '03:30 PM',
  '05:00 PM',
  '06:30 PM',
];

const BookDemoModal = ({ isOpen, onClose }) => {
  const dateInputRef = useRef(null);
  const customTimeInputRef = useRef(null);

  const [turnstileToken, setTurnstileToken] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState("11:30 AM");
  const [isCustomTime, setIsCustomTime] = useState(false);
  const [customTime, setCustomTime] = useState("14:30");

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleOpenPicker = (ref) => {
    if (ref && ref.current) {
      try {
        if (typeof ref.current.showPicker === 'function') {
          ref.current.showPicker();
        } else {
          ref.current.focus();
        }
      } catch (err) {
        // Ignore if already open or unsupported
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectPresetSlot = (slot) => {
    setIsCustomTime(false);
    setSelectedTimeSlot(slot);
  };

  const handleSelectCustomSlot = () => {
    setIsCustomTime(true);
    setTimeout(() => {
      handleOpenPicker(customTimeInputRef);
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!turnstileToken) {
      toast.error('Please complete the security CAPTCHA verification');
      return;
    }

    if (!selectedDate) {
      toast.error('Please select your preferred demo date');
      return;
    }

    // Format final time string
    let finalTimeString = selectedTimeSlot;
    if (isCustomTime) {
      if (!customTime) {
        toast.error('Please select a valid custom time');
        return;
      }
      // Format 24hr HH:MM to 12hr string for readability
      const [hours, minutes] = customTime.split(':');
      const dateHelper = new Date();
      dateHelper.setHours(parseInt(hours, 10), parseInt(minutes, 10));
      finalTimeString = dateHelper.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    }

    // Combine Date + Time Slot into ISO timestamp
    const fullDateTimeString = `${selectedDate} ${finalTimeString}`;
    const dateObj = new Date(fullDateTimeString);
    const isoDate = isNaN(dateObj.getTime()) ? new Date(selectedDate).toISOString() : dateObj.toISOString();

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/demo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          date: isoDate,
          turnstileToken,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to schedule demo');
      }

      toast.success('Demo request submitted! Our enterprise team will contact you shortly.', {
        duration: 4000,
        style: {
          background: '#0f172a',
          color: '#38bdf8',
          border: '1px solid #38bdf8',
        },
      });
      onClose();
      // Reset form
      setFormData({ name: '', email: '', phone: '', message: '' });
      setSelectedDate("");
      setTurnstileToken("");
      setIsCustomTime(false);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get today's date formatted as YYYY-MM-DD for min attribute
  const todayStr = new Date().toISOString().split('T')[0];

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
        <div className={styles.glowGradient} />

        <div className={styles.header}>
          <div>
            <div className={styles.badgeTag}>
              <Sparkles size={14} /> Provaantech Platform Demo
            </div>
            <h2 className={styles.headerTitle}>Schedule a Personalized Demo</h2>
            <div className={styles.headerSub}>
              Discover how Provaantech's AI Solutions (ToxBox AI, ProHirey, GCC Acceleration) transform enterprise growth.
            </div>
          </div>
          <button onClick={onClose} className={styles.closeBtn} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGrid}>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>
                <User size={15} color="#38bdf8" /> Full Name *
              </label>
              <div className={styles.inputWrapper}>
                <User size={18} className={styles.inputIcon} />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="John Doe"
                  className={styles.inputField}
                />
              </div>
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label}>
                <Mail size={15} color="#38bdf8" /> Work Email *
              </label>
              <div className={styles.inputWrapper}>
                <Mail size={18} className={styles.inputIcon} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="john@company.com"
                  className={styles.inputField}
                />
              </div>
            </div>
          </div>

          <div className={styles.formGrid}>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>
                <Phone size={15} color="#38bdf8" /> Phone Number *
              </label>
              <div className={styles.inputWrapper}>
                <Phone size={18} className={styles.inputIcon} />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  placeholder="+91 98765 43210"
                  className={styles.inputField}
                />
              </div>
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label}>
                <Calendar size={15} color="#38bdf8" /> Preferred Date *
              </label>
              <div 
                className={styles.inputWrapper} 
                onClick={() => handleOpenPicker(dateInputRef)} 
                style={{ cursor: 'pointer' }}
              >
                <Calendar size={18} className={styles.inputIcon} />
                <input
                  ref={dateInputRef}
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenPicker(dateInputRef);
                  }}
                  required
                  min={todayStr}
                  className={styles.inputField}
                />
              </div>
            </div>
          </div>

          {/* Customizable Selectable Time Slot Component */}
          <div className={styles.timeSlotSection}>
            <label className={styles.label}>
              <Clock size={15} color="#38bdf8" /> Select Preferred Time Slot (IST) *
            </label>

            <div className={styles.timeSlotGrid}>
              {PRESET_TIME_SLOTS.map((slot) => (
                <button
                  key={slot}
                  type="button"
                  className={`${styles.timeSlotPill} ${!isCustomTime && selectedTimeSlot === slot ? styles.timeSlotPillActive : ''}`}
                  onClick={() => handleSelectPresetSlot(slot)}
                >
                  <Clock size={13} /> {slot}
                </button>
              ))}

              <button
                type="button"
                className={`${styles.timeSlotPill} ${isCustomTime ? styles.timeSlotPillActive : ''}`}
                onClick={handleSelectCustomSlot}
              >
                <Sliders size={13} /> Custom Time...
              </button>
            </div>

            {/* Custom Time Selection Picker */}
            {isCustomTime && (
              <div 
                className={styles.customTimeWrapper} 
                onClick={() => handleOpenPicker(customTimeInputRef)}
                style={{ cursor: 'pointer' }}
              >
                <span className={styles.customTimeLabel}>Choose Exact Time:</span>
                <input
                  ref={customTimeInputRef}
                  type="time"
                  value={customTime}
                  onChange={(e) => setCustomTime(e.target.value)}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenPicker(customTimeInputRef);
                  }}
                  className={styles.customTimeInput}
                  required
                />
              </div>
            )}
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>
              <MessageSquareText size={15} color="#38bdf8" /> Business Needs / Solution Interest (optional)
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              rows={3}
              placeholder="Tell us about your specific goals (e.g., ToxBox AI, ProHirey recruitment, GCC scaling, Cloud DevOps)..."
              className={styles.textareaField}
            />
          </div>

          <div className={styles.captchaSection}>
            <Turnstile
              siteKey="0x4AAAAAAB_TvBQU7q63Kawb"
              onSuccess={setTurnstileToken}
              onError={() => setTurnstileToken("")}
              onExpire={() => setTurnstileToken("")}
            />
          </div>

          <button type="submit" className={styles.submitBtn} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 size={20} className="animate-spin" /> Submitting Request...
              </>
            ) : (
              <>
                Confirm Demo Booking <ArrowRight size={20} />
              </>
            )}
          </button>

          <div className={styles.footerNote}>
            <Shield size={14} color="#64748b" /> Your data is encrypted and handled with enterprise privacy.
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookDemoModal;
